import { useCallback, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { FileArrowUp, Warning } from 'shared/icons';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Mono, Sku } from '../ui/Mono';
import { useToast } from '../ui/Toast';
import { FormatInt } from '../../utils/format';
import { IndexarCatalogoPorEan, LerLinhasDaPlanilha, ResolverItensDoArquivo } from '../../utils/purchaseUpload';

/**
 * Upload do desejo de compra para alimentar o carrinho.
 *
 * Vive SÓ na tela de Ordem de Compra (decisão do dono) — o carrinho é o mesmo
 * de /operacoes e do sellout, mas a porta de entrada por arquivo é daqui.
 *
 * O arquivo traz apenas **EAN + QTD**; SKU, descrição, marca e código do
 * fornecedor saem do nosso catálogo. Como a página já carrega o catálogo
 * inteiro para o seletor de SKU, a resolução acontece em memória — não há
 * chamada de rede nem upload do arquivo para lugar nenhum.
 *
 * A conferência ANTES de jogar no carrinho não é ornamento: um EAN que não
 * existe no catálogo tem que aparecer com o número da linha. Sumir em silêncio
 * faria o comprador fechar a OC convencido de que pediu um item que nunca
 * entrou.
 */
const CartFileUpload = ({ catalogo = [], onAdicionar, desabilitado = false }) => {
    const toast = useToast();
    const inputRef = useRef(null);
    const [aberto, setAberto] = useState(false);
    const [nomeArquivo, setNomeArquivo] = useState('');
    const [resultado, setResultado] = useState(null);
    const [lendo, setLendo] = useState(false);

    const catalogoPorEan = useMemo(() => IndexarCatalogoPorEan(catalogo), [catalogo]);

    const lerArquivo = useCallback(
        async (arquivo) => {
            if (!arquivo) return;
            setLendo(true);
            setNomeArquivo(arquivo.name);
            try {
                const buffer = await arquivo.arrayBuffer();
                const workbook = XLSX.read(buffer, { type: 'array' });
                const primeiraAba = workbook.Sheets[workbook.SheetNames[0]];
                // `header: 1` devolve matriz crua: o cabeçalho pode não estar na
                // primeira linha, e é o parser que o localiza.
                const matriz = XLSX.utils.sheet_to_json(primeiraAba, { header: 1, raw: true });
                const { itens, erro } = LerLinhasDaPlanilha(matriz);
                if (erro) {
                    setResultado(null);
                    toast.error(erro);
                    return;
                }
                setResultado(ResolverItensDoArquivo(itens, catalogoPorEan));
                setAberto(true);
            } catch (error) {
                toast.error(`Não consegui ler o arquivo: ${error?.message || 'formato não reconhecido'}`);
            } finally {
                setLendo(false);
                if (inputRef.current) inputRef.current.value = '';
            }
        },
        [catalogoPorEan, toast],
    );

    const confirmar = () => {
        const encontrados = resultado?.encontrados ?? [];
        let adicionados = 0;
        for (const item of encontrados) {
            const ok = onAdicionar({
                sku: item.sku,
                title: item.title,
                product_name: item.title,
                bling_stock: Number(item.bling_stock || 0),
                cost_price: Number(item.cost_price || 0),
                quantity: item.quantidade,
            });
            if (ok) adicionados += 1;
        }
        toast.success(`${FormatInt(adicionados)} ${adicionados === 1 ? 'item adicionado' : 'itens adicionados'} ao carrinho.`);
        setAberto(false);
        setResultado(null);
    };

    const naoResolvidos = (resultado?.semProduto?.length ?? 0) + (resultado?.invalidos?.length ?? 0);

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(event) => lerArquivo(event.target.files?.[0])}
            />
            <Button
                variant="secondary"
                onClick={() => inputRef.current?.click()}
                disabled={desabilitado || lendo || catalogo.length === 0}
                title={catalogo.length === 0 ? 'Aguarde o catálogo carregar' : 'Planilha com EAN e QTD'}
            >
                <FileArrowUp size={16} className="mr-1.5" />
                {lendo ? 'Lendo…' : 'Subir arquivo'}
            </Button>

            <Modal
                isOpen={aberto}
                onClose={() => setAberto(false)}
                title={`Conferir importação — ${nomeArquivo}`}
                size="4xl"
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setAberto(false)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmar}
                            disabled={(resultado?.encontrados?.length ?? 0) === 0}
                        >
                            Seguir — adicionar {FormatInt(resultado?.encontrados?.length ?? 0)} ao carrinho
                        </Button>
                    </>
                }
            >
                <div className="space-y-5">
                    <div className="flex flex-wrap gap-2">
                        <Badge variant="success">
                            {FormatInt(resultado?.encontrados?.length ?? 0)} encontrados
                        </Badge>
                        {naoResolvidos > 0 ? (
                            <Badge variant="warning">{FormatInt(naoResolvidos)} não entram</Badge>
                        ) : null}
                    </div>

                    {resultado?.encontrados?.length ? (
                        <section>
                            <h3 className="mb-2 text-sm font-semibold text-black dark:text-white">
                                Vão para o carrinho
                            </h3>
                            {/* EAN, SKU, marca e quantidade são dados de
                                conferência: precisam aparecer INTEIROS, então
                                levam `whitespace-nowrap` e dimensionam pelo
                                conteúdo. Só a Descrição encolhe — ela recebe
                                `w-full max-w-0`, o par que faz uma célula
                                absorver o espaço restante e ainda assim
                                truncar (sem `max-w-0` o `truncate` não morde
                                dentro de tabela). Nada de rolagem lateral. */}
                            <div className="max-h-72 overflow-y-auto rounded-lg border border-mono-200 dark:border-mono-800">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-mono-50 dark:bg-mono-900">
                                        <tr className="text-left text-xs uppercase tracking-wide text-mono-500">
                                            <th className="whitespace-nowrap px-3 py-2 font-medium">EAN</th>
                                            <th className="whitespace-nowrap px-3 py-2 font-medium">SKU</th>
                                            <th className="w-full max-w-0 px-3 py-2 font-medium">Descrição</th>
                                            <th className="whitespace-nowrap px-3 py-2 font-medium">Marca</th>
                                            <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Qtd</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-mono-100 dark:divide-mono-900">
                                        {resultado.encontrados.map((item) => (
                                            <tr key={item.ean}>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    <Mono className="text-xs">{item.ean}</Mono>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2">
                                                    <Sku value={item.sku} className="text-xs" />
                                                </td>
                                                <td
                                                    className="w-full max-w-0 truncate px-3 py-2 text-mono-600 dark:text-mono-400"
                                                    title={item.title}
                                                >
                                                    {item.title}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-mono-600 dark:text-mono-400">
                                                    {item.brand || '—'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums">
                                                    {FormatInt(item.quantidade)}
                                                    {item.linhas.length > 1 ? (
                                                        <span
                                                            className="ml-1 text-[11px] text-mono-500"
                                                            title={`Somado das linhas ${item.linhas.join(', ')}`}
                                                        >
                                                            ({item.linhas.length}×)
                                                        </span>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ) : null}

                    {resultado?.semProduto?.length ? (
                        <section>
                            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-warning-text">
                                <Warning size={15} />
                                EAN sem produto no catálogo
                            </h3>
                            <p className="mb-2 text-xs text-mono-500 dark:text-mono-400">
                                Estes ficam de fora. Cadastre o produto (ou o EAN dele) em Produtos e suba o arquivo
                                de novo.
                            </p>
                            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                                {resultado.semProduto.map((item) => (
                                    <li key={`${item.ean}-${item.linha}`} className="flex gap-2">
                                        <span className="text-mono-500">linha {item.linha}</span>
                                        <Mono>{item.ean}</Mono>
                                        <span className="text-mono-500">× {FormatInt(item.quantidade)}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null}

                    {resultado?.invalidos?.length ? (
                        <section>
                            <h3 className="mb-2 text-sm font-semibold text-mono-600 dark:text-mono-400">
                                Linhas ignoradas
                            </h3>
                            <ul className="max-h-32 space-y-1 overflow-y-auto text-xs text-mono-500">
                                {resultado.invalidos.map((item) => (
                                    <li key={`${item.linha}-${item.motivo}`}>
                                        linha {item.linha} — {item.motivo}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null}
                </div>
            </Modal>
        </>
    );
};

export default CartFileUpload;
